import { useId, useRef, useState } from "react";
import { FileText, UploadCloud, X } from "lucide-react";
import { uploadDocument } from "../../services/document.api.js";
import { addKbDocument } from "../../store/state.js";
import { formatBytes, showToast } from "../../utils/dom.js";
import { t, tc } from "../../i18n/index.js";
import type { KbDocument } from "../../types/index.js";

export const KB_CATEGORIES = ["rapport_n1", "qrt", "orsa", "fonction_actuarielle", "provisionnement",
  "sinistralite", "gouvernance", "comptes_annuels", "autre"];

// Mirrors the server ceiling (backend/app/core/config.py), so an oversized file
// is refused here instead of after a long upload.
const MAX_BYTES = 25_000_000;

export default function DocumentUpload({ onIngested }: { onIngested: (document: KbDocument) => void }) {
  const inputId = useId();
  const input = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [category, setCategory] = useState(KB_CATEGORIES[0]);
  const [origin, setOrigin] = useState(t("admin.kb.upload.defaultOrigin"));
  const [percent, setPercent] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const busy = percent !== null;

  function choose(candidate: File | undefined) {
    if (!candidate) return;
    if (candidate.type !== "application/pdf" && !candidate.name.toLowerCase().endsWith(".pdf")) {
      setFile(null);
      setError(t("admin.kb.upload.notPdf"));
      return;
    }
    if (candidate.size > MAX_BYTES) {
      setFile(null);
      setError(t("admin.kb.upload.tooLarge", { limit: formatBytes(MAX_BYTES) }));
      return;
    }
    setError(null);
    setFile(candidate);
  }

  function clear() {
    setFile(null);
    setError(null);
    if (input.current) input.current.value = "";
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!file || busy) return;
    setError(null);
    setPercent(0);
    try {
      const saved = await uploadDocument(file, category, origin, setPercent);
      addKbDocument(saved);
      showToast(saved.status === "needs_ocr"
        ? t("toast.kbNeedsOcr", { pages: saved.pagesWithoutText?.length ?? 0 })
        : t("toast.kbIndexed", { pages: saved.pageCount, chunks: saved.chunks }));
      clear();
      onIngested(saved);
    } catch (uploadError) {
      setError((uploadError as Error).message);
    } finally {
      setPercent(null);
    }
  }

  return <form className="kb-upload" onSubmit={submit} aria-busy={busy}>
    <div
      className={`kb-drop ${dragging ? "dragging" : ""} ${file ? "filled" : ""}`}
      onDragOver={event => { event.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={event => { event.preventDefault(); setDragging(false); choose(event.dataTransfer.files[0]); }}
    >
      <input ref={input} id={inputId} type="file" accept="application/pdf,.pdf" hidden
        onChange={event => choose(event.target.files?.[0])} />
      {file
        ? <p className="kb-drop-file"><FileText size={18} aria-hidden="true" />
            <span><strong>{file.name}</strong><em>{formatBytes(file.size)}</em></span>
            <button type="button" className="icon-btn" onClick={clear} disabled={busy}
              aria-label={t("admin.kb.upload.remove")}><X size={16} /></button>
          </p>
        : <p className="kb-drop-hint"><UploadCloud size={22} aria-hidden="true" />
            <span>{t("admin.kb.upload.dropzone")}</span>
            <label className="btn outline" htmlFor={inputId}>{t("admin.kb.upload.browse")}</label>
          </p>}
    </div>

    <div className="kb-upload-fields">
      <label>{t("admin.kb.th.category")}
        <select value={category} onChange={event => setCategory(event.target.value)} disabled={busy}>
          {KB_CATEGORIES.map(value => <option key={value} value={value}>{tc(`docCategory.${value}`, value)}</option>)}
        </select>
      </label>
      <label>{t("admin.kb.th.origin")}
        <input type="text" value={origin} onChange={event => setOrigin(event.target.value)} disabled={busy} />
      </label>
      <button className="btn dark" type="submit" disabled={!file || busy}>
        {busy ? t("admin.kb.upload.uploading", { percent }) : t("admin.kb.upload.submit")}
      </button>
    </div>

    {busy && <div className="kb-progress" role="progressbar" aria-valuenow={percent} aria-valuemin={0} aria-valuemax={100}>
      <i style={{ width: `${percent}%` }} />
    </div>}
    {error && <p className="kb-upload-error" role="alert">{error}</p>}
    <p className="muted">{t("admin.kb.upload.hint")}</p>
  </form>;
}
